import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { getProgramTypeIcon } from '../types/program';
import { useProgramTypeName } from '../config/useLabels';

// Walk up the DOM to find the nearest vertically-scrollable ancestor.
// Returns null when nothing scrolls (i.e. the window is the scroll context).
// This lets the same hook drive both the parent-style window scroll and the
// admin inner-overflow table containers without the caller knowing which is which.
function getScrollParent(node: HTMLElement | null): HTMLElement | null {
  let el = node?.parentElement ?? null;
  while (el) {
    const style = window.getComputedStyle(el);
    if (/(auto|scroll|overlay)/.test(style.overflowY) && el.scrollHeight > el.clientHeight) {
      return el;
    }
    el = el.parentElement;
  }
  return null;
}

export interface ProgramTypeSectionNav {
  activeType: string;
  /** Attach to each type's section header element: `ref={nav.setSectionRef(type)}`. */
  setSectionRef: (type: string) => (el: HTMLElement | null) => void;
  /** Smooth-scroll the section header for `type` to the top of its scroll container. */
  scrollToType: (type: string) => void;
}

/**
 * Scroll-spy + smooth-scroll for program-type-grouped sections. Mirrors the parent
 * Programs page category nav but works inside any scroll container by deriving the
 * scroll parent of the registered section elements at runtime. Clicking relies on
 * `scrollIntoView`, so callers should set `scroll-margin-top` on the section headers
 * to clear any sticky table heads / sticky nav that overlaps the top.
 */
export function useProgramTypeSectionNav(types: string[], threshold = 96): ProgramTypeSectionNav {
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
  const programmaticRef = useRef(false);
  const [activeType, setActiveType] = useState('');

  const setSectionRef = useCallback(
    (type: string) => (el: HTMLElement | null) => {
      sectionRefs.current[type] = el;
    },
    []
  );

  const typesKey = types.join('|');

  useEffect(() => {
    if (types.length === 0) return;

    let rafId: number | null = null;

    const resolveContainer = (): HTMLElement | null => {
      const first = types.map((t) => sectionRefs.current[t]).find(Boolean) ?? null;
      return getScrollParent(first);
    };

    const computeActive = () => {
      if (programmaticRef.current) return;
      const container = resolveContainer();
      const scrollTop = container ? container.scrollTop : window.scrollY;
      const containerTop = container ? container.getBoundingClientRect().top : 0;
      let current = types[0];
      for (const type of types) {
        const el = sectionRefs.current[type];
        if (!el) continue;
        const elTop = container
          ? el.getBoundingClientRect().top - containerTop + container.scrollTop
          : el.getBoundingClientRect().top + window.scrollY;
        if (scrollTop + threshold >= elTop) current = type;
        else break;
      }
      setActiveType(current);
    };

    const onScroll = () => {
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        computeActive();
      });
    };

    const scrollTarget: Window | HTMLElement = resolveContainer() ?? window;
    computeActive();
    scrollTarget.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      scrollTarget.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typesKey, threshold]);

  const scrollToType = useCallback((type: string) => {
    const el = sectionRefs.current[type];
    if (!el) return;
    programmaticRef.current = true;
    setActiveType(type);
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.setTimeout(() => {
      programmaticRef.current = false;
    }, 500);
  }, []);

  return { activeType, setSectionRef, scrollToType };
}

interface ProgramTypeScrollNavProps {
  types: string[];
  activeType: string;
  onSelect: (type: string) => void;
  className?: string;
}

/**
 * Horizontally-scrolling program-type pill bar with chevron arrows and edge fades.
 * Pair with `useProgramTypeSectionNav` for jump-to-section + active highlighting.
 */
export function ProgramTypeScrollNav({ types, activeType, onSelect, className = '' }: ProgramTypeScrollNavProps) {
  const programTypeName = useProgramTypeName();
  const navRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateArrows = useCallback(() => {
    const el = navRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    updateArrows();
    el.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', updateArrows);
    return () => {
      el.removeEventListener('scroll', updateArrows);
      window.removeEventListener('resize', updateArrows);
    };
  }, [updateArrows, types.length]);

  const nudge = (dir: 'left' | 'right') => {
    navRef.current?.scrollBy({ left: dir === 'left' ? -160 : 160, behavior: 'smooth' });
  };

  // A single category provides no navigation value.
  if (types.length < 2) return null;

  return (
    <div className={`relative bg-white border-b border-gray-200 ${className}`}>
      {canScrollLeft && (
        <>
          <div className="pointer-events-none absolute inset-y-0 left-0 w-12 bg-gradient-to-r from-white via-white/80 to-transparent z-10" />
          <button
            type="button"
            onClick={() => nudge('left')}
            aria-label="Scroll categories left"
            className="absolute inset-y-0 left-0 z-20 flex items-center justify-center w-9 text-gray-500 hover:text-gray-900 transition-colors"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
        </>
      )}
      <nav ref={navRef} className="flex overflow-x-auto no-scrollbar px-2 sm:px-4">
        {types.map((type) => {
          const isActive = activeType ? activeType === type : types[0] === type;
          return (
            <button
              key={type}
              type="button"
              onClick={() => onSelect(type)}
              className={`flex-shrink-0 flex items-center gap-1.5 py-2.5 px-3 border-b-2 text-sm font-medium whitespace-nowrap transition-colors ${
                isActive
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span aria-hidden="true">{getProgramTypeIcon(type)}</span>
              {programTypeName(type)}
            </button>
          );
        })}
      </nav>
      {canScrollRight && (
        <>
          <div className="pointer-events-none absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-white via-white/80 to-transparent z-10" />
          <button
            type="button"
            onClick={() => nudge('right')}
            aria-label="Scroll categories right"
            className="absolute inset-y-0 right-0 z-20 flex items-center justify-center w-9 text-gray-500 hover:text-gray-900 transition-colors"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </>
      )}
    </div>
  );
}
